import { SignupPayload, SignupPreflightResponse, SignupResponse, SignupType } from "@/app/auth/signup/signup.entity.ts";
import type { InviteAccepted, InviteAcceptRequest, PartyKind, SignedUp, SignupOrganizationKind, SignupRequest } from "@shyne/shared/api";
import { refusedByTheApi } from "@shyne/shared/api";
import { api } from "@/lib/api";
import supabase from "@/lib/supabase";
import { uuid } from "@/interfaces";
import { organization_type } from "@/models/organization.model";
import { recordAuthEmailEvent } from "@/services/auth-email-events.service";

const ALREADY_HAS_AN_ACCOUNT = "You already have an account. Sign in, then open the invitation link again to accept it.";

async function establishSession(email: string, password: string): Promise<uuid> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });

  if (error || !data.session) {
    if (error?.message?.includes("confirmation") || error?.message?.includes("Email not confirmed")) {
      throw new Error("Account created. Please confirm your email via the link we sent, then log in.");
    }

    throw error ?? new Error("Failed to sign in after account creation.");
  }

  return data.user.id as uuid;
}

export type PendingSignup =
  | { kind: "invited"; email: string }
  | { kind: "exists"; email: string }
  | { kind: "ready"; email: string; details: SignupRequest }
  | { kind: "incomplete"; email: string; details: Partial<SignupRequest> };

export type SignupMetadata = { full_name?: string; phone?: string; organization_kind?: string; organization_name?: string };

const SIGNUP_ORGANIZATION_KINDS: readonly SignupOrganizationKind[] = ["customer", "vendor", "subcontractor"];

const textIn = (value: unknown): string | undefined => (typeof value === "string" && value.trim() !== "" ? value.trim() : undefined);

export function signupDetailsIn(metadata: Record<string, unknown> | undefined): Partial<SignupRequest> {
  const kind = SIGNUP_ORGANIZATION_KINDS.find((candidate) => candidate === metadata?.organization_kind);
  return {
    name: textIn(metadata?.full_name),
    phone: textIn(metadata?.phone),
    organizationKind: kind,
    organizationName: textIn(metadata?.organization_name),
  };
}

const isComplete = (details: Partial<SignupRequest>): details is SignupRequest => Boolean(details.name && details.organizationKind && details.organizationName);

const INVITEE_TYPE_OF_KIND: Record<PartyKind, organization_type> = {
  customer: organization_type.pmc,
  vendor: organization_type.vendor,
  subcontractor: organization_type.subs,
  worker: organization_type.emp,
};

const KIND_OF_ORGANIZATION_TYPE: Partial<Record<organization_type, SignupOrganizationKind>> = {
  [organization_type.pmc]: "customer",
  [organization_type.vendor]: "vendor",
  [organization_type.subs]: "subcontractor",
};

export const SignupRepository = {
  async preflightEmail(email: string): Promise<SignupPreflightResponse> {
    const answer = await api.signup.preflight({ email });

    switch (answer.answer) {
      case "invited":
        return { type: SignupType.org_invite, inviteeOrgType: INVITEE_TYPE_OF_KIND[answer.invitedAs] };
      case "exists":
        throw new Error("An account with this e-mail already exists. Please log in instead.");
      case "new":
        return { type: SignupType.plain };
    }
  },

  async resendConfirmationEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.resend({ type: "signup", email });

    if (error) {
      void recordAuthEmailEvent({
        eventType: "auth_signup_resend",
        recipientEmail: email,
        status: "failed",
        errorMessage: error.message,
      });
      throw error;
    }

    void recordAuthEmailEvent({
      eventType: "auth_signup_resend",
      recipientEmail: email,
    });
  },

  async signUp(payload: SignupPayload): Promise<SignupResponse> {
    const { name, email, phone, password, organizationType, organizationName } = payload;
    const organizationKind = KIND_OF_ORGANIZATION_TYPE[organizationType];
    if (!organizationKind) throw new Error("Choose a property manager, vendor or sub-contractor organization.");
    if (!organizationName) throw new Error("Organization name required for new signup");

    await SignupRepository.preflightEmail(email);

    const details: SignupMetadata = { full_name: name, phone: phone || undefined, organization_kind: organizationKind, organization_name: organizationName };
    const { data: authRes, error: authErr } = await supabase.auth.signUp({
      email,
      password,
      options: { data: details },
    });

    if (authErr) {
      void recordAuthEmailEvent({
        eventType: "auth_signup",
        recipientEmail: email,
        status: "failed",
        payload: { organizationType },
        errorMessage: authErr.message,
      });

      if (authErr.message?.includes("already registered") || authErr.message?.includes("already exists")) {
        throw new Error("An account with this e-mail already exists. Please log in instead.");
      }

      throw authErr;
    }

    const authId = authRes.user?.id as uuid | undefined;
    if (!authId) {
      throw new Error("Failed to create authentication account.");
    }

    void recordAuthEmailEvent({
      eventType: "auth_signup",
      recipientEmail: email,
      payload: { organizationType },
    });

    if (!authRes.session) return { auth_id: authId };

    const { person, organization } = await api.signup.create({ name, phone: phone || undefined, organizationKind, organizationName });

    return { user_id: person, auth_id: authId, organization_id: organization };
  },

  async pendingSignup(): Promise<PendingSignup> {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user?.email) throw error ?? new Error("Sign in again to finish signing up.");
    const email = data.user.email;
    const details = signupDetailsIn(data.user.user_metadata);
    const answer = await api.signup.preflight({ email });
    if (answer.answer === "invited") return { kind: "invited", email };
    if (answer.answer === "exists") return { kind: "exists", email };
    return isComplete(details) ? { kind: "ready", email, details } : { kind: "incomplete", email, details };
  },

  async finishSignup(details: SignupRequest): Promise<SignedUp> {
    return api.signup.create({ ...details, phone: details.phone || undefined });
  },

  async signUpFromInvite(token: uuid, payload: SignupPayload): Promise<SignupResponse> {
    const { name, email, phone, password, organizationName } = payload;
    await api.invites.prepareLogin(token, { email, password, name }).catch((error: unknown) => {
      throw refusedByTheApi(error) && error.status === 409 ? new Error(ALREADY_HAS_AN_ACCOUNT) : error;
    });
    const authId = await establishSession(email, password);
    const { person, organization } = await api.invites.accept(token, { name, phone: phone || undefined, organizationName: organizationName || undefined });
    return { user_id: person, auth_id: authId, organization_id: organization };
  },

  async acceptInvite(token: uuid, acceptance: InviteAcceptRequest): Promise<InviteAccepted> {
    return api.invites.accept(token, acceptance);
  },
};
